import React, {forwardRef, ReactNode, Suspense, useCallback, useEffect, useImperativeHandle} from 'react';
import {useLocation} from 'react-router-dom';
import {router} from '@/router';
import {StyledTab, StyledTabDiv, StyledTabs, HiddenTab} from '@common/TabsComponent.styles';
import Loading from '@common/Loading';
import Helpers from '@/helpers/helpers';

export interface TabsRef {
  changeTab(tabIndex: number): void;
}

export interface TabProps {
  value: number;
  label: string;
  route?: string;
  hidden?: boolean;
  content: (() => ReactNode) | ReactNode;
}

type Props = {
  tabs: TabProps[];
  forceRender?: boolean;
  tabsClassName?: string;
  setCurrentTab?: (tabIndex: number) => void;
  onHiddenTabRedirect?: (hiddenTab: TabProps, redirectedTab: TabProps) => void;
};

const TabsComponent = forwardRef<TabsRef, Props>((props, ref) => {
  const location = useLocation();
  const [currentTab, setCurrentTab] = React.useState<number | null>(null);
  const [currentRoute, setCurrentRoute] = React.useState<string | null>(null);
  const redirected = React.useRef(false);
  const currentTabObj = props.tabs.find(tab => tab.value === currentTab);

  useImperativeHandle(ref, () => {
    return {
      changeTab(tabIndex: number) {
        setCurrentTab(tabIndex);
      },
    };
  }, []);

  useEffect(() => {
    const firstVisibleTab = props.tabs.find(tab => !tab.hidden);
    const matchedTab = props.tabs.find(tab => tab.route === location.pathname);
    let tabIndex = matchedTab?.value;

    if (matchedTab?.hidden && firstVisibleTab) {
      tabIndex = firstVisibleTab.value;

      if (!redirected.current) {
        props.onHiddenTabRedirect?.(matchedTab, firstVisibleTab);
        redirected.current = true;
      }
    }

    if (!Helpers.isDefined(tabIndex)) {
      tabIndex = firstVisibleTab?.value;

      if (firstVisibleTab?.route && currentTab === tabIndex) {
        router.navigate(-1);
      }
    }

    setCurrentTab(Helpers.isDefined(tabIndex) ? tabIndex : null);
  }, [location.pathname]);

  useEffect(() => {
    if (Helpers.isDefined(currentTab) && currentTabObj) {
      if (props.setCurrentTab) {
        props.setCurrentTab(currentTab);
      }

      if (currentTabObj.route && currentTabObj.route !== location.pathname) {
        router.navigate({
          pathname: currentTabObj.route,
          search: location.search,
        }, {
          replace: currentRoute === null,
        });

        setCurrentRoute(currentTabObj.route ?? null);
        redirected.current = false;
      }
    }
  }, [currentTab]);

  const renderTabContent = useCallback((tab: TabProps) => {
    return typeof tab.content === 'function' ? tab.content() : tab.content;
  }, [currentTab]);

  const handleTabChange = useCallback((tabIndex: number) => {
    const tab = props.tabs.find(t => t.value === tabIndex);
    if (tab?.route && tab.route !== location.pathname) {
      router.navigate({
        pathname: tab.route,
        search: location.search,
      }, {
        replace: currentRoute === null,
      });
    } else {
      setCurrentTab(tabIndex);
    }
  }, [props.tabs, location.pathname, location.search, currentRoute]);

  return (
    <>
      {Helpers.isDefined(currentTab) && (
        <StyledTabs value={currentTab} onChange={handleTabChange} className={props.tabsClassName}>
          { props.tabs.map((tab, index) => {
            if(tab.hidden) return <HiddenTab key={index}></HiddenTab>;

            return (
              <StyledTab key={index} data-testid="tab">
                {tab.label}
              </StyledTab>
            );
          })}
        </StyledTabs>
      )}

      <Suspense fallback={<Loading iconSize="2x" />}>
        {props.forceRender && props.tabs.map((tab, index) => (
          <StyledTabDiv id={tab.label} key={index} display={tab.value === currentTab ? 'block' : 'none'}>
            {renderTabContent(tab)}
          </StyledTabDiv>
        ))}

        {!props.forceRender && currentTabObj && renderTabContent(currentTabObj)}
      </Suspense>
    </>
  );
});

export default React.memo(TabsComponent);
